import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toast';
import CustomDropdown from './Components/CustomDropdown';
import { secondDatabase } from '../../config/firebase';

const CreateRequest = ({ isOpen, onClose, project, setItems}) => {
  
  const [materialsData, setMaterialsData] = useState({
    categories: [],
    descriptions: [],
    uoms: [],
    descriptionIDs: []
  });
  
  // Map to store relationships between categories and descriptions
  const [categoryDescriptionsMap, setCategoryDescriptionsMap] = useState({});
  
  // Map to store relationships between descriptions and uoms
  const [descriptionUomMap, setDescriptionUomMap] = useState({});
  
  // Map to store relationships between descriptions and their IDs
  const [descriptionIdMap, setDescriptionIdMap] = useState({});

  const [userName, setUserName] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    quantity: '',
    unit: '',
    descriptionId: '',
    requestedBy: userName || 'Anonymous',
    requestedOn: new Date().getTime(),
    status: 'Requested',
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const getUserName = () => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        return user.name || "Admin User";
      }
      return "Admin User";
    } catch (error) {
      console.error("Error getting user name:", error);
      return "Admin User";
    }
  };

  // Add a specific function to initialize the component when modal opens
  const initializeComponent = async () => {
    if (!project || !project.firmDetailsID) {
      console.error('Missing required project properties:', project);
      return;
    }
    
    console.log('Initializing CreateRequest component with project:', project);
    
    setFormData({
      category: '',
      description: '',
      quantity: '',
      unit: '',
      descriptionId: '',
      requestedBy: userName || 'Anonymous',
      requestedOn: new Date().getTime(),
      status: 'Requested',
    });
    
    await fetchMaterialsData();
  };

  // Add cleanup function to useEffect to reset state on modal close
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened - initializing component state');
      initializeComponent();
      
      // Add debug info for properties received
      console.log('*** DEBUG INFO ***');
      console.log('Project prop:', project);
      console.log('userName prop:', userName);
      console.log('******************');
    } else {
      // Reset state when modal closes
      console.log('Modal closed - resetting component state');
      setMaterialsData({
        categories: [],
        descriptions: [],
        uoms: [],
        descriptionIDs: []
      });
      setCategoryDescriptionsMap({});
      setDescriptionUomMap({});
      setDescriptionIdMap({});
      setValidationErrors({});
    }
  }, [isOpen, project, userName]);

  useEffect(() => {
    getUserName();
  }, [])

  // Fetch materials data from Firebase with enhanced error handling
  const fetchMaterialsData = async () => {
    setLoading(true);
    
    if (!project || !project.firmDetailsID) {
      console.error('Missing required project properties:', project);
      setMaterialsData({
        categories: [],
        descriptions: [],
        uoms: [],
        descriptionIDs: []
      });
      setLoading(false);
      return;
    }
    
    try {
      console.log('Fetching materials for project:', project);
      const path = `${String(project.firmDetailsID)}/projectMasterFile/materials`;
      console.log('Database path:', path);
      
      const materialsRef = secondDatabase.ref(path);
      
      materialsRef.once('value', (snapshot) => {
        const data = snapshot.val();
        console.log('Fetched materials data:', data);
        
        if (data) {
          // Convert the object structure to arrays
          const materialsArray = Object.values(data || {});
          console.log('Materials array:', materialsArray);
          
          // Extract all necessary fields
          const processedData = {
            categories: materialsArray.map(item => item.category || ''),
            descriptions: materialsArray.map(item => item.description || ''),
            uoms: materialsArray.map(item => item.uom || ''),
            descriptionIDs: materialsArray.map(item => item.descriptionID || '')
          };
          
          setMaterialsData(processedData);
          processMaterialsData(processedData);
        } else {
          console.log('No materials found in database');
          setMaterialsData({
            categories: [],
            descriptions: [],
            uoms: [],
            descriptionIDs: []
          });
        }
        setLoading(false);
      }, (error) => {
        console.error('Firebase fetch error:', error);
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching materials data:', error);
      setLoading(false);
    }
  };

  // Process the materials data to create lookup maps
  const processMaterialsData = (data = null) => {
    const dataToProcess = data || materialsData;
    
    // Safety check for invalid data
    if (!dataToProcess || !Array.isArray(dataToProcess.categories)) {
      console.error('Invalid data structure for processing:', dataToProcess);
      setCategoryDescriptionsMap({});
      setDescriptionUomMap({});
      setDescriptionIdMap({});
      return;
    }
    
    const categoryMap = {};
    const uomMap = {};
    const idMap = {};
    
    // Add null checks to ensure arrays exist
    const categories = dataToProcess.categories || [];
    const descriptions = dataToProcess.descriptions || [];
    const uoms = dataToProcess.uoms || [];
    const descriptionIDs = dataToProcess.descriptionIDs || [];
    
    console.log('Processing materials data:', {
      categories: categories.length,
      descriptions: descriptions.length,
      uoms: uoms.length,
      descriptionIDs: descriptionIDs.length
    });
    
    // Create maps for quicker lookups
    categories.forEach((category, index) => {
      // Make sure we don't access beyond array bounds
      if (index < descriptions.length && index < uoms.length && index < descriptionIDs.length) {
        const description = descriptions[index];
        const uom = uoms[index];
        const descriptionId = descriptionIDs[index];
        
        // Only process if category and description are valid strings
        if (category && description) {
          // Build category to descriptions map
          if (!categoryMap[category]) {
            categoryMap[category] = [];
          }
          
          // Avoid adding duplicates
          if (!categoryMap[category].includes(description)) {
            categoryMap[category].push(description);
          }
          
          // Build description to UOM map
          uomMap[description] = uom || '';
          
          // Build description to ID map
          idMap[description] = descriptionId || '';
        }
      }
    });
    
    console.log('Processed category map:', categoryMap);
    console.log('Processed UOM map:', uomMap);
    console.log('Processed ID map:', idMap);
    
    setCategoryDescriptionsMap(categoryMap);
    setDescriptionUomMap(uomMap);
    setDescriptionIdMap(idMap);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCategorySelect = (option, index) => {
    console.log('Category selected:', option);
    setFormData({
      ...formData,
      category: option,
      description: '', // Reset description when category changes
      unit: '', // Reset unit when category changes
      descriptionId: '', // Reset description ID when category changes
    });
  };

  const handleDescriptionSelect = (option, index) => {
    console.log('Description selected:', option);
    if (option !== "Select Category to fetch Items...") {
      // Get the UOM for this description
      const uom = descriptionUomMap[option] || '';
      // Get the description ID for this description
      const descriptionId = descriptionIdMap[option] || '';
      
      setFormData({
        ...formData,
        description: option,
        unit: uom,
        descriptionId: descriptionId,
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.category) errors.category = "Category is required";
    if (!formData.description) errors.description = "Description is required";
    if (!formData.quantity) errors.quantity = "Quantity is required";
    if (!formData.unit) errors.unit = "Unit is required";
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRaiseRequest = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields", {
        backgroundColor: "#8e3f3f",
        color: "white",
      });
      return;
    }

    setSaving(true);
    try {
      // Validate project data again to be safe
      if (!project || !project.firmDetailsID || !project.projectID) {
        throw new Error("Invalid project data for creating request");
      }
    
      const timestamp = new Date().getTime();
      
      // Check if this material already exists by comparing category + description
      const isNewMaterial = !materialsData.categories.some((category, index) => 
        category === formData.category && 
        materialsData.descriptions[index] === formData.description
      );
      
      console.log('Creating request with data:', {
        category: formData.category,
        description: formData.description,
        quantity: formData.quantity,
        unit: formData.unit,
        isNewMaterial
      });
      
      // Variable to hold the itemId for the request
      let itemId = formData.descriptionId; 
      
      // If using existing material, find its ID
      if (!isNewMaterial) {
        // Find the existing material's description ID
        const existingIndex = materialsData.categories.findIndex((category, index) => 
          category === formData.category && 
          materialsData.descriptions[index] === formData.description
        );
        
        if (existingIndex !== -1) {
          itemId = materialsData.descriptionIDs[existingIndex];
          console.log('Using existing material with ID:', itemId);
        }
      } else {
        // For new materials, set itemId to "notVerified" instead of registering 
        itemId = "notVerified";
        console.log('Using notVerified for new material:', formData.description);
      }

      // Create the material request
      const requestRef = secondDatabase.ref(
        `${project.firmDetailsID}/MRNRequest/${project.projectID}/${timestamp}`
      );

      const requestData = {
        id: timestamp,
        items: [
          {
            id: timestamp,
            heading: 'Non tender items',
            subHeding: 'Extra items',
            category: formData.category,
            status: 'Requested',
            quantity: formData.quantity,
            unit: formData.unit,
            description: formData.description,
            itemId: itemId || null,
          }
        ],
        timestamp: timestamp,
        requestHistory: {
          [timestamp]: {
            id: timestamp,
            name: userName,
            status: 'Requested',
          }
        },
      };

      console.log('Saving request data:', requestData);
      await requestRef.set(requestData);
      setItems((prevItems => [requestData, ...prevItems]))
      toast.success("Request raised successfully", {
        backgroundColor: "#4d873f",
        color: "white",
      });
      
      setSaving(false);
      onClose();
    } catch (error) {
      console.error("Error raising request:", error);
      toast.error(`Failed to raise request: ${error.message}`, {
        backgroundColor: "#8e3f3f",
        color: "white",
      });
      setSaving(false);
    }
  };

  // Register a new material when user enters a new category or description
  const registerNewMaterial = async () => {
    try {
      // Register new material in the database
      const materialsRef = secondDatabase.ref(`${project.firmDetailsID}/projectMasterFile/materials`);
      
      // Create a unique ID for the new material
      const newMaterialId = `${new Date().getTime()}`;
      
      // Create the new material object with the same structure as existing materials
      const newMaterial = {
        category: formData.category,
        description: formData.description,
        uom: formData.unit,
        descriptionID: newMaterialId,
        image: {} // Empty image object as shown in the example data
      };
      
      console.log('Registering new material:', newMaterial);
      
      // Add the new material to the database (using the same structure as the existing data)
      await materialsRef.child(newMaterialId).set(newMaterial);
      
      // Get the updated materials data
      const snapshot = await materialsRef.once('value');
      const updatedData = snapshot.val();
      
      if (updatedData) {
        // Process the updated data
        const materialsArray = Object.values(updatedData || {});
        const processedData = {
          categories: materialsArray.map(item => item.category || ''),
          descriptions: materialsArray.map(item => item.description || ''),
          uoms: materialsArray.map(item => item.uom || ''),
          descriptionIDs: materialsArray.map(item => item.descriptionID || '')
        };
        
        setMaterialsData(processedData);
        processMaterialsData(processedData);
      }
      
      // Update form data with the new material ID
      setFormData({
        ...formData,
        descriptionId: newMaterialId
      });
      
      return newMaterialId;
    } catch (error) {
      console.error("Error registering new material:", error);
      throw error;
    }
  };

  // Add a dedicated debug effect for the project prop
  useEffect(() => {
    if (isOpen) {
      console.log('CreateRequest received project prop:', project);
      console.log('Project props available:', project ? Object.keys(project) : 'No project data');
      
      if (project && project.firmDetailsID) {
        console.log('Project has required properties for fetching data');
      } else {
        console.warn('Project missing required properties:', {
          firmDetailsID: project?.firmDetailsID,
          projectID: project?.projectID
        });
      }
    }
  }, [isOpen, project]);

  // Debugging - log the state when it changes
  useEffect(() => {
    if (isOpen) {
      console.log('Current state:', {
        availableCategories: Object.keys(categoryDescriptionsMap),
        formData
      });
    }
  }, [categoryDescriptionsMap, formData, isOpen]);

  // Return early with an error message if missing project data
  if (isOpen && (!project || !project.firmDetailsID)) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '500px',
            padding: '24px',
            position: 'relative',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'transparent',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#888',
            }}
          >
            ✕
          </button>
          <div
            style={{
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '24px',
              color: '#d32f2f'
            }}
          >
            Error: Invalid Project Data
          </div>
          <p style={{ textAlign: 'center', marginBottom: '20px' }}>
            Unable to create a material request. Please select a valid project and try again.
          </p>
          <div style={styles.buttonGroup}>
            <button
              onClick={onClose}
              style={styles.cancelButton}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  // Get unique categories for the dropdown
  const uniqueCategories = Object.keys(categoryDescriptionsMap);
  // Get descriptions for the selected category
  const categoryDescriptions = formData.category ? (categoryDescriptionsMap[formData.category] || []) : [];

  // Debug log categories and descriptions
  console.log('Rendering dropdown with:', {
    uniqueCategories,
    categoryDescriptions,
    selectedCategory: formData.category,
    materialsDataCategories: materialsData.categories,
    categoryDescriptionsMap
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '500px',
          padding: '24px',
          position: 'relative',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#888',
          }}
        >
          ✕
        </button>
        <div
          style={{
            textAlign: 'center',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '24px'
          }}
        >
          Material Request {project && `| ${project.projectName}`}
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading categories...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <div style={styles.inputContainer}>
                <CustomDropdown
                  options={uniqueCategories}
                  placeholder="Select or type a new category..."
                  value={formData.category}
                  onChange={(value) => {
                    setFormData({ ...formData, category: value });
                  }}
                  onOptionSelect={handleCategorySelect}
                  index={0}
                  validationError={validationErrors.category}
                />
                {uniqueCategories.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    No categories available. You can type a new category name.
                  </div>
                )}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Item Description</label>
              <div style={styles.inputContainer}>
                <CustomDropdown
                  options={categoryDescriptions.length > 0 ? categoryDescriptions : ["Select Category to fetch Items..."]}
                  placeholder="Select or type a new description..."
                  value={formData.description}
                  onChange={(value) => {
                    setFormData({ ...formData, description: value });
                  }}
                  onOptionSelect={handleDescriptionSelect}
                  index={0}
                  validationError={validationErrors.description}
                />
                {formData.category && categoryDescriptions.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    No items for this category. You can type a new description.
                  </div>
                )}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Unit (UOM)</label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                placeholder="Enter unit of measurement"
                style={{
                  ...styles.input,
                  borderColor: validationErrors.unit ? "red" : "#ddd"
                }}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Quantity</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                placeholder="Enter required quantity"
                style={{
                  ...styles.input,
                  borderColor: validationErrors.quantity ? "red" : "#ddd"
                }}
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                onClick={onClose}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseRequest}
                disabled={saving}
                style={styles.requestButton}
              >
                {saving ? "Requesting..." : "Raise Request"}
              </button>
            </div>
          </div>
        )}
      </div>
      
      <ToastContainer
        delay={2000}
        position="bottom-center"
        style={{
          position: "fixed",
          zIndex: 100000,
        }}
      />
    </div>
  );
};

const styles = {
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    fontSize: '16px',
    color: '#333',
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '15px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: '#f1f1f1',
    color: '#333',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  requestButton: {
    padding: '12px 20px',
    backgroundColor: '#4A90E2',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default CreateRequest;
